import { apiClient } from './apiClient';

/**
 * Portfolio Service
 * Handles portfolio CRUD operations and actions
 */

export interface PortfolioItem {
  id: string;
  userId: string;
  imageUrl: string;
  thumbnailUrl?: string;
  title?: string;
  caption?: string;
  category?: string;
  tags?: string[];
  likes: number;
  views: number;
  isProfilePicture: boolean;
  isCoverImage: boolean;
  status: 'draft' | 'published';
  createdAt: string;
  updatedAt: string;
}

export interface PortfolioStats {
  totalItems: number;
  totalViews: number;
  totalLikes: number;
  categories: {
    [key: string]: number;
  };
}

export interface PaginatedPortfolioResponse {
  items: PortfolioItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}


export const portfolioService = {
  /**
   * Get user's portfolio items (paginated)
   * GET /portfolio?page=1&limit=12&category=fashion
   */
  getPortfolio: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    userId?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.userId) queryParams.append('userId', params.userId);

    const url = `/portfolio${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiClient.get<PaginatedPortfolioResponse>(url);
  },


  /**
   * Get single portfolio item
   * GET /portfolio/:id
   */
  getPortfolioItem: async (id: string) => {
    return await apiClient.get<PortfolioItem>(`/portfolio/${id}`);
  },


  /**
   * Delete portfolio item
   * DELETE /portfolio/:id
   */
  deletePortfolioItem: async (id: string) => {
    return await apiClient.delete(`/portfolio/${id}`);
  },

  /**
   * Set portfolio item as cover image
   * POST /portfolio/:id/set-cover
   */
  setCoverImage: async (id: string) => {
    return await apiClient.post(`/portfolio/${id}/set-cover`);
  },

  /**
   * Set portfolio item as profile picture
   * POST /portfolio/:id/set-profile-picture
   */
  setProfilePicture: async (id: string) => {
    return await apiClient.post(`/portfolio/${id}/set-profile-picture`);
  },

  /**
   * Like a portfolio item (public endpoint)
   * POST /portfolio/:id/like
   */
  likePortfolioItem: async (id: string) => {
    return await apiClient.post(`/portfolio/${id}/like`);
  },

  /**
   * Get portfolio statistics
   * GET /portfolio/stats
   */
  getPortfolioStats: async () => {
    return await apiClient.get<PortfolioStats>('/portfolio/stats');
  },

  /**
   * Update portfolio item status
   * PATCH /portfolio/:id
   */
  updatePortfolioStatus: async (id: string, status: 'draft' | 'published') => {
    return await apiClient.patch<PortfolioItem>(`/portfolio/${id}`, { status });
  },
};
